import {
  fetchChildRows,
  orcanosItemUrl,
  exportEcoZip as postEcoExport,
  exportBomZip as postBomExport,
} from '../api/orcanosClient';
import { getSettings } from '../settings/settingsStore';
import {
  fmtCost,
  computeCostRollup,
  getUnitCost,
  getQuantity,
  COST_COLUMN_KEYS,
  UNIT_COST_KEY,
  COST_KEY,
  TOTAL_COST_KEY,
} from '../utils/cost';
import { fieldTextFor } from '../utils/columns';

// The synthetic "File" column: the revision file of a DMS item — the one an ECO released in
// the ECO view, the document's current approved one in a BOM zip (the server decides both).
export const FILE_KEY = '__file';
// The synthetic "Attachments" column of a zip: the plain files (STEP models, drawings…) on a
// part or assembly. Only the server knows them — it fills each row's token with file chips.
export const ATTACH_KEY = '__attachments';

const EXPORT_CONCURRENCY = 6;
const EXPORT_PAGE_SIZE = 500; // large page to minimize pagination in deep BOMs

// ─── Concurrency-limited map ───────────────────────────────────────────────
// Same pattern as useBomChildren.js — duplicated here so exportUtils has no
// dependency on the hook.
async function mapWithLimit(items, limit, fn) {
  const out = new Array(items.length);
  let nextIdx = 0;
  async function worker() {
    while (true) {
      const i = nextIdx++;
      if (i >= items.length) return;
      out[i] = await fn(items[i], i);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

// ─── Full-subtree fetch ─
// Every export below takes `nodes` to skip this walk: Product structures keep the hierarchy in
// our database and hand over a ready list in the same shape (bom/structures/psExport.js).
//───────────────────────────────────────────────────
// BFS from rootRow, fetching children independently of the UI tree state.
// Returns a flat array in BFS order — parents always precede their children,
// which is the requirement for computeLevelNums below.
//
// onProgress(done, total) is called after each item is fetched:
//   done  — items whose children have been fetched so far
//   total — all items discovered so far (grows as children arrive)
// Both converge to the full tree size when the BFS completes.
//
// `view` decides what a row's children are (fetchChildRows): part instances for a BOM, parts
// and DMS items for an ECO.
async function fetchFullSubtree(rootRow, onProgress, view = 'boms') {
  let seq = 0;
  const makeId = () => `x${++seq}`;
  const rootId = makeId();

  const nodes = [{ nodeKey: rootId, parentKey: null, depth: 0, isRoot: true, row: rootRow }];
  let frontier = [{ nodeKey: rootId, row: rootRow, depth: 0 }];

  let done = 0;
  let total = 1; // root is the first item to process
  onProgress?.(done, total);

  while (frontier.length > 0) {
    const levelResults = await mapWithLimit(frontier, EXPORT_CONCURRENCY, async (item) => {
      if (!item.row.originalId && view !== 'ecos') {
        done++;
        onProgress?.(done, total);
        return { parentKey: item.nodeKey, children: [], depth: item.depth + 1 };
      }
      try {
        const res = await fetchChildRows(view, item.row, { pageSize: EXPORT_PAGE_SIZE });
        done++;
        total += res.rows.length; // each child will also be processed
        onProgress?.(done, total);
        return { parentKey: item.nodeKey, children: res.rows, depth: item.depth + 1 };
      } catch {
        done++;
        onProgress?.(done, total);
        return { parentKey: item.nodeKey, children: [], depth: item.depth + 1 };
      }
    });

    const nextFrontier = [];
    for (const { parentKey, children, depth } of levelResults) {
      for (const row of children) {
        const id = makeId();
        nodes.push({ nodeKey: id, parentKey, depth, isRoot: false, row });
        nextFrontier.push({ nodeKey: id, row, depth });
      }
    }
    frontier = nextFrontier;
  }

  // BFS gives breadth-first order; reorder to DFS so each parent is immediately
  // followed by its descendants (required for visual tree and level numbering).
  const childrenOf = new Map();
  for (const n of nodes) {
    const pk = n.parentKey ?? '';
    if (!childrenOf.has(pk)) childrenOf.set(pk, []);
    childrenOf.get(pk).push(n);
  }
  const dfs = [];
  function visit(nodeKey) {
    for (const child of (childrenOf.get(nodeKey) || [])) {
      dfs.push(child);
      visit(child.nodeKey);
    }
  }
  for (const root of (childrenOf.get('') || [])) {
    dfs.push(root);
    visit(root.nodeKey);
  }
  return dfs;
}

// ─── Level numbers ─────────────────────────────────────────────────────────
// Same logic as BomTree.jsx computeLevelNumbers — roots' direct children
// restart at 1, grandchildren use 1.1, 1.2, etc.
function computeLevelNums(nodes) {
  const nums = new Map();
  const counters = new Map();
  const rootKeys = new Set(nodes.filter((n) => n.isRoot).map((n) => n.nodeKey));
  for (const n of nodes) {
    const pk = n.parentKey ?? '';
    const idx = (counters.get(pk) ?? 0) + 1;
    counters.set(pk, idx);
    const parentIsRoot = n.parentKey ? rootKeys.has(n.parentKey) : true;
    const parentNum = !parentIsRoot ? nums.get(n.parentKey) : null;
    nums.set(n.nodeKey, parentNum ? `${parentNum}.${idx}` : String(idx));
  }
  return nums;
}

// ─── Shared helpers ────────────────────────────────────────────────────────

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function sanitizeFilename(name) {
  return String(name || 'bom').replace(/[^a-z0-9\-_.]/gi, '_').slice(0, 60);
}

function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function stripHtml(s) {
  return String(s ?? '').replace(/<[^>]+>/g, '');
}

function csvEscape(s) {
  const str = String(s ?? '');
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

// `costs` is the rollup map from computeCostRollup — required for the three
// synthetic cost columns, ignored for everything else.
function getFieldValue(node, col, costs) {
  if (COST_COLUMN_KEYS.has(col.key)) {
    const c = costs?.get(node.nodeKey);
    if (!c) return '';
    const n =
      col.key === UNIT_COST_KEY ? c.unit
      : col.key === COST_KEY ? c.cost
      : c.total;
    return n ? fmtCost(n) : '';
  }
  if (col.key === '__quantity') {
    // The export root can be any item in the hierarchy, so read its own
    // Quantity first — only a PRT (which has no such field) falls back to "1".
    return (
      node.row.byName('Quantity') ||
      node.row.byTitle('Quantity') ||
      (node.isRoot ? '1' : '')
    );
  }
  if (col.key === ATTACH_KEY) return '';
  if (col.key === FILE_KEY) {
    const f = node.row.ecoFile;
    return f ? `${f.name} (rev ${f.revision}${f.changeNo ? `.${f.changeNo}` : ''})` : '';
  }
  if (col.key === '__revision') {
    return (
      node.row.byTitle('Part Revision') ||
      node.row.byName('Part Revision') ||
      node.row.byTitle('Revision') ||
      node.row.byName('Revision') ||
      ''
    );
  }
  // Merged columns carry each type's Name/Title aliases — a DMS item and a
  // part export under the one header they share.
  return fieldTextFor(node.row, col);
}

function isKeyCol(col) {
  return col.key === 'User_Prefix' || String(col.title || '').trim().toLowerCase() === 'key';
}

function buildItemUrl(node) {
  const s = getSettings();
  if (!node.row.itemId) return '';
  // The export root is no longer always a PRT — any row in the hierarchy can
  // be exported — so trust the row's own Type and only guess when it's absent.
  // No versionId: orcanosItemUrl picks the version the row's TYPE is read from (DMS and CR
  // live in their own projects).
  return orcanosItemUrl({
    baseUrl: s.baseUrl,
    type: node.row.type || (node.isRoot ? 'PRT' : 'PI'),
    itemId: node.row.itemId,
  });
}

function levelNum(node, levelNumbers) {
  return node.isRoot ? '' : (levelNumbers.get(node.nodeKey) || '');
}

// ─── Summary builder ───────────────────────────────────────────────────────
// Groups all non-root nodes by part key (User_Prefix / Key / objName), sums
// quantities, and returns one entry per unique part sorted by key.
//
// Quantities are **effective**, not per-parent: a row's Quantity is multiplied
// by every quantity above it, so three screws inside a qty-2 sub-assembly count
// as six. `nodes` is in DFS order (parent before child), so one forward pass
// fills the map.
//
// EVERY row is charged, sub-assemblies included, because a row contributes its
// own cost as well as its contents (see computeCostRollup's rule note).
//
// This is what keeps Summary and Hierarchic agreeing: the hierarchic root total
// expands to Σ over EVERY node of (effective qty × unit), which is what summing
// `unitCost × totalQty` over these entries produces — bar the export root's own
// cost, which Summary leaves out along with the root row.
function buildSummary(nodes) {
  const map = new Map();
  const effQty = new Map();
  for (const n of nodes) {
    const own = getQuantity(n.row);
    effQty.set(n.nodeKey, n.parentKey ? (effQty.get(n.parentKey) ?? 1) * own : own);
  }
  for (const n of nodes) {
    if (n.isRoot) continue;
    const partKey =
      n.row.byName('User_Prefix') || n.row.byTitle('Key') || n.row.objName || n.nodeKey;
    const qty = effQty.get(n.nodeKey) ?? 1;
    if (map.has(partKey)) {
      map.get(partKey).totalQty += qty;
      map.get(partKey).count += 1;
    } else {
      map.set(partKey, { node: n, totalQty: qty, count: 1, unitCost: getUnitCost(n.row) });
    }
  }
  return [...map.values()].sort((a, b) => {
    const ka = a.node.row.byName('User_Prefix') || a.node.row.objName || '';
    const kb = b.node.row.byName('User_Prefix') || b.node.row.objName || '';
    return ka.localeCompare(kb);
  });
}

function fmtQty(q) {
  return q % 1 === 0 ? String(q) : q.toFixed(4).replace(/\.?0+$/, '');
}


// Columns the Summary view replaces with its own aggregates: per-occurrence
// Quantity and the three per-row cost columns (summary reports Total Qty and
// unit × total qty instead).
function summaryColumns(columns) {
  return columns.filter(
    (c) => c.key !== '__quantity' && !COST_COLUMN_KEYS.has(c.key)
  );
}

// ─── JSON export ───────────────────────────────────────────────────────────

export async function exportJson(rootRow, columns, { bomName, summary = false, onProgress, view = 'boms', nodes: given } = {}) {
  const nodes = given || await fetchFullSubtree(rootRow, onProgress, view);

  if (summary) {
    const entries = buildSummary(nodes);
    const nonQtyCols = summaryColumns(columns);
    const hasCost = entries.some((e) => e.unitCost > 0);
    const grandTotal = hasCost ? entries.reduce((s, e) => s + e.unitCost * e.totalQty, 0) : 0;
    const data = {
      bom: bomName,
      exportedAt: new Date().toISOString().split('T')[0],
      view: 'summary',
      ...(hasCost && { totalCost: `$${fmtCost(grandTotal)}` }),
      items: entries.map((e) => {
        const fields = {};
        for (const col of nonQtyCols) fields[col.title] = stripHtml(getFieldValue(e.node, col));
        fields['Total Qty'] = e.totalQty;
        fields['Occurrences'] = e.count;
        if (hasCost) {
          fields['Unit Cost'] = `$${fmtCost(e.unitCost)}`;
          fields['Total Cost'] = `$${fmtCost(e.unitCost * e.totalQty)}`;
        }
        return { type: e.node.row.type, fields };
      }),
    };
    downloadFile(JSON.stringify(data, null, 2), `${sanitizeFilename(bomName)}-summary.json`, 'application/json');
    return;
  }

  const levelNumbers = computeLevelNums(nodes);
  // The whole subtree was just fetched, so every rollup here is exact.
  const costs = computeCostRollup(nodes, { assumeComplete: true });

  const childrenOf = new Map();
  for (const n of nodes) {
    const pk = n.parentKey ?? '';
    if (!childrenOf.has(pk)) childrenOf.set(pk, []);
    childrenOf.get(pk).push(n);
  }

  function buildNode(n) {
    const fields = {};
    for (const col of columns) fields[col.title] = stripHtml(getFieldValue(n, col, costs));
    const item = { number: levelNum(n, levelNumbers), type: n.row.type, fields };
    const kids = childrenOf.get(n.nodeKey);
    if (kids?.length) item.children = kids.map(buildNode);
    return item;
  }

  const rootTotal = costs.get(nodes[0].nodeKey)?.total || 0;
  const data = {
    bom: bomName,
    exportedAt: new Date().toISOString().split('T')[0],
    ...(rootTotal > 0 && { totalCost: `$${fmtCost(rootTotal)}` }),
    items: [buildNode(nodes[0])],
  };

  downloadFile(JSON.stringify(data, null, 2), `${sanitizeFilename(bomName)}.json`, 'application/json');
}

// ─── CSV export ────────────────────────────────────────────────────────────

export async function exportCsv(rootRow, columns, { bomName, summary = false, onProgress, view = 'boms', nodes: given } = {}) {
  const nodes = given || await fetchFullSubtree(rootRow, onProgress, view);

  if (summary) {
    const entries = buildSummary(nodes);
    const nonQtyCols = summaryColumns(columns);
    const hasCost = entries.some((e) => e.unitCost > 0);
    const grandTotal = hasCost ? entries.reduce((s, e) => s + e.unitCost * e.totalQty, 0) : 0;
    const headers = [
      'Type', ...nonQtyCols.map((c) => c.title), 'Total Qty', 'Occurrences',
      ...(hasCost ? ['Unit Cost', 'Total Cost'] : []),
    ];
    const lines = [
      ...(hasCost ? [[`Total Cost: $${fmtCost(grandTotal)}`].map(csvEscape).join(',')] : []),
      headers.map(csvEscape).join(','),
      ...entries.map((e) => {
        const cells = [
          e.node.row.type,
          ...nonQtyCols.map((c) => stripHtml(getFieldValue(e.node, c))),
          fmtQty(e.totalQty),
          String(e.count),
          ...(hasCost ? [`$${fmtCost(e.unitCost)}`, `$${fmtCost(e.unitCost * e.totalQty)}`] : []),
        ];
        return cells.map(csvEscape).join(',');
      }),
    ];
    downloadFile(lines.join('\r\n'), `${sanitizeFilename(bomName)}-summary.csv`, 'text/csv;charset=utf-8');
    return;
  }

  const levelNumbers = computeLevelNums(nodes);
  const costs = computeCostRollup(nodes, { assumeComplete: true });
  const rootTotal = costs.get(nodes[0].nodeKey)?.total || 0;

  const headers = ['#', 'Type', ...columns.map((c) => c.title)];
  const lines = [
    ...(rootTotal > 0 ? [csvEscape(`Total Cost: $${fmtCost(rootTotal)}`)] : []),
    headers.map(csvEscape).join(','),
    ...nodes.map((n) => {
      const cells = [
        levelNum(n, levelNumbers),
        n.row.type,
        ...columns.map((c) => stripHtml(getFieldValue(n, c, costs))),
      ];
      return cells.map(csvEscape).join(',');
    }),
  ];

  downloadFile(lines.join('\r\n'), `${sanitizeFilename(bomName)}.csv`, 'text/csv;charset=utf-8');
}

// ─── HTML export (interactive) ─────────────────────────────────────────────

export async function exportHtml(rootRow, columns, { bomName, summary = false, onProgress, view = 'boms', nodes: given } = {}) {
  const nodes = given || await fetchFullSubtree(rootRow, onProgress, view);
  const html = summary
    ? _generateSummaryHtml(buildSummary(nodes), columns, { bomName, printMode: false })
    : _generateHtml(nodes, columns, computeLevelNums(nodes), {
        bomName,
        printMode: false,
        costs: computeCostRollup(nodes, { assumeComplete: true }),
      });
  downloadFile(html, `${sanitizeFilename(bomName)}${summary ? '-summary' : ''}.html`, 'text/html;charset=utf-8');
}

// ─── PDF export (HTML → browser print dialog) ──────────────────────────────

export async function exportPdf(rootRow, columns, { bomName, summary = false, onProgress, view = 'boms', nodes: given } = {}) {
  const nodes = given || await fetchFullSubtree(rootRow, onProgress, view);
  const html = summary
    ? _generateSummaryHtml(buildSummary(nodes), columns, { bomName, printMode: true })
    : _generateHtml(nodes, columns, computeLevelNums(nodes), {
        bomName,
        printMode: true,
        costs: computeCostRollup(nodes, { assumeComplete: true }),
      });
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url);
  if (win) {
    win.addEventListener('load', () => {
      setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 300);
    });
  } else {
    downloadFile(html, `${sanitizeFilename(bomName)}.html`, 'text/html;charset=utf-8');
  }
}

// ─── Package index (zip) ───────────────────────────────────────────────────
// A zip's index is HTML (built here, file links as tokens the server fills) or JSON / CSV /
// Excel / PDF — rendered by the server from this flat table, because Excel and PDF cannot be
// written in the browser. The File and Attachments columns are left OUT of the table: only
// the server knows what it downloaded, so it adds them.

export const INDEX_FORMATS = [
  { id: 'html', label: 'HTML (interactive)' },
  { id: 'xlsx', label: 'Excel' },
  { id: 'csv',  label: 'CSV' },
  { id: 'json', label: 'JSON' },
  { id: 'pdf',  label: 'PDF' },
];

export function buildExportTable(nodes, columns, levelNumbers, costs) {
  const cols = columns.filter((c) => c.key !== FILE_KEY && c.key !== ATTACH_KEY);
  return {
    columns: cols.map((c) => c.title),
    rows: nodes.map((n) => ({
      number: levelNum(n, levelNumbers),
      depth: n.depth || 0,
      type: n.row.type || '',
      item_id: n.row.itemId || '',
      cells: cols.map((c) => stripHtml(getFieldValue(n, c, costs))),
    })),
  };
}

// The rows whose plain attachments go in a BOM zip. A DMS row's file is its approved
// revision (a different path), so it is not asked for here. `version` is the row's own
// numeric project version — the server's Attachments read answers an empty list for any
// other. A part instance also names its master part, where the model usually hangs.
export function attachmentItems(nodes) {
  const seen = new Set();
  const out = [];
  for (const n of nodes) {
    const r = n.row;
    if (!r.itemId || r.type === 'DMS' || r.type === 'CR' || seen.has(r.itemId)) continue;
    seen.add(r.itemId);
    out.push({
      id: String(r.itemId),
      type: r.type || '',
      version: String(r.raw?.Version ?? ''),
      master_id: r.type === 'PI' && r.originalId && r.originalId !== r.itemId ? String(r.originalId) : '',
    });
  }
  return out;
}

async function packageIndex(nodes, columns, { bomName, indexFormat, dmsAttachments = false }) {
  const levelNumbers = computeLevelNums(nodes);
  const costs = computeCostRollup(nodes, { assumeComplete: true });
  if (indexFormat && indexFormat !== 'html') {
    return { indexFormat, table: buildExportTable(nodes, columns, levelNumbers, costs) };
  }
  const html = _generateHtml(nodes, columns, levelNumbers, {
    bomName,
    printMode: false,
    costs,
    fileLinks: true,
    dmsAttachments,
  });
  return { indexFormat: 'html', html };
}

// ─── ECO export (zip: index + released files) ──────────────────────────────
// The hierarchy is the BOM export's own HTML (or a table the server renders). The server adds
// the files and fills in their links — it decides WHICH files from the ECO id, never from
// this page (bom_api.eco_export).
// ⚠️ Every file it reads is logged as a download in that document's Orcanos history.

// `includeAttachments` adds an Attachments column: the plain files attached to each DMS item
// the ECO lists (images, STEP models — not its main document file, which is in `files/`).
// The server lists them from the ECO id.
export async function exportEcoZip(
  rootRow,
  columns,
  { bomName, onProgress, indexFormat = 'html', includeAttachments = true } = {}
) {
  const nodes = await fetchFullSubtree(rootRow, onProgress, 'ecos');
  let cols = columns.filter((c) => c.key !== ATTACH_KEY);
  if (includeAttachments) cols = [...cols, { key: ATTACH_KEY, title: 'Attachments' }];
  const index = await packageIndex(nodes, cols, { bomName, indexFormat, dmsAttachments: true });
  const blob = await postEcoExport({ ecoId: rootRow.itemId, name: bomName, includeAttachments, ...index });
  downloadFile(blob, `${sanitizeFilename(bomName)}.zip`, 'application/zip');
}

// ─── BOM export (zip: index + DMS files + attachments) ─────────────────────
// The same deliverable as an ECO zip, for a BOM or an assembly: a tenant that hangs drawings
// and specifications off an assembly as DMS items gets those files in the package, and the
// plain attachments on every part and assembly (a STEP model, a drawing) come too.
//
// The File and Attachments columns are APPENDED here rather than picked in the modal — they
// exist only in this format. The server fills each DMS row's File token with `files/<name>`
// (the newest APPROVED revision — it decides, this page never names one) and each row's
// Attachments token with `attachments/<TYPE>-<id>/<name>` chips.

export async function exportBomZip(
  rootRow,
  columns,
  { bomName, onProgress, view = 'boms', indexFormat = 'html', includeAttachments = true, nodes: given } = {}
) {
  const nodes = given || await fetchFullSubtree(rootRow, onProgress, view);
  const itemIds = [
    ...new Set(nodes.filter((n) => n.row.type === 'DMS' && n.row.itemId).map((n) => n.row.itemId)),
  ];
  let cols = columns.filter((c) => c.key !== ATTACH_KEY);
  if (!cols.some((c) => c.key === FILE_KEY)) cols = [...cols, { key: FILE_KEY, title: 'File' }];
  if (includeAttachments) cols = [...cols, { key: ATTACH_KEY, title: 'Attachments' }];
  // 'all': a DMS row's own attachments (a STEP model beside the document) come too.
  const index = await packageIndex(nodes, cols, { bomName, indexFormat, dmsAttachments: 'all' });
  const blob = await postBomExport({
    itemIds,
    includeAttachments,
    attachments: includeAttachments ? attachmentItems(nodes) : [],
    name: bomName,
    ...index,
  });
  downloadFile(blob, `${sanitizeFilename(bomName)}.zip`, 'application/zip');
}

// ─── Shared document shell ─────────────────────────────────────────────────
// One look for every exported page — the tree export, the summary, the ECO zip
// and the BOM zip. Document-grade rather than app-grade: a light paper table
// under a header that says what the package IS, with the brand purple spent on
// the few things that carry meaning (keys, the tree, assembly rows) instead of
// on a solid header band.
//
// Themes: the page ships its own light palette and follows the reader's OS into
// dark. Print always renders the light one — an exported PDF is a document.

const EXPORT_CSS = `
:root{
  --paper:#fff;--ground:#f6f5f9;--ink:#1c1930;--ink-2:#4b4661;--ink-3:#7d7794;
  --line:#e6e3ef;--line-2:#f1eff6;
  --accent:#5c35a8;--accent-soft:#f0ebfa;--accent-ink:#4a2a8a;--on-accent:#fff;
  --ok:#1f7a55;--ok-soft:#e4f3ec;--warn:#94620a;--warn-soft:#fbf1da;
  --hold:#8a4a6d;--hold-soft:#f8eaf1;--row-hover:#faf8fd;
  --shadow:0 1px 2px rgba(28,25,48,.05),0 8px 24px -12px rgba(28,25,48,.18);
}
@media (prefers-color-scheme:dark){
  :root{
    --paper:#191725;--ground:#111019;--ink:#eceaf4;--ink-2:#b6b1c9;--ink-3:#8b85a1;
    --line:#2c2940;--line-2:#232033;
    --accent:#b39bee;--accent-soft:#272042;--accent-ink:#c9b6ff;--on-accent:#191725;
    --ok:#6fd3a5;--ok-soft:#16342a;--warn:#e0b25a;--warn-soft:#342a14;
    --hold:#e49dc0;--hold-soft:#361f2b;--row-hover:#1f1c2e;
    --shadow:0 1px 2px rgba(0,0,0,.4),0 8px 24px -12px rgba(0,0,0,.6);
  }
}
*{box-sizing:border-box}
body{margin:0;background:var(--ground);color:var(--ink);
  font:400 13px/1.5 "IBM Plex Sans","Segoe UI",Inter,Arial,sans-serif;-webkit-font-smoothing:antialiased}
.sheet{max-width:1400px;margin:0 auto;padding:26px 22px 56px;display:flex;flex-direction:column;gap:16px}

.doc-head{display:flex;flex-wrap:wrap;align-items:flex-start;justify-content:space-between;
  gap:20px;padding-bottom:16px;border-bottom:1px solid var(--line)}
.eyebrow{margin:0 0 7px;color:var(--accent);
  font:600 10.5px/1 ui-monospace,"IBM Plex Mono",Consolas,monospace;letter-spacing:.14em;text-transform:uppercase}
h1{margin:0;font-size:23px;font-weight:600;letter-spacing:-.015em;text-wrap:balance}
.stamp{text-align:right;color:var(--ink-3);white-space:nowrap;
  font:500 11.5px/1.7 ui-monospace,"IBM Plex Mono",Consolas,monospace}
.stamp b{display:block;color:var(--ink);font-weight:600;font-size:13px}

.facts{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));
  background:var(--paper);border:1px solid var(--line);border-radius:8px;overflow:hidden;box-shadow:var(--shadow)}
.fact{padding:12px 15px;border-right:1px solid var(--line-2)}
.fact:last-child{border-right:0}
.fact dt{margin:0 0 5px;color:var(--ink-3);
  font:600 10px/1 ui-monospace,"IBM Plex Mono",Consolas,monospace;letter-spacing:.1em;text-transform:uppercase}
.fact dd{margin:0;font-size:14px;font-weight:600;font-variant-numeric:tabular-nums}
.fact dd small{margin-left:3px;font-weight:400;font-size:11.5px;color:var(--ink-3)}
.fact.money dd{color:var(--accent-ink)}

.toolbar{position:sticky;top:0;z-index:5;display:flex;flex-wrap:wrap;align-items:center;gap:8px;
  padding:9px 0;background:var(--ground)}
.search{flex:1 1 230px;max-width:340px;display:flex;align-items:center;gap:8px;height:32px;padding:0 10px;
  background:var(--paper);border:1px solid var(--line);border-radius:7px}
.search svg{flex:none;color:var(--ink-3)}
.search input{width:100%;border:0;background:none;outline:none;color:var(--ink);
  font:400 13px/1 "IBM Plex Sans",Arial,sans-serif}
.search input::placeholder{color:var(--ink-3)}
.search:focus-within{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-soft)}
.btn{height:32px;padding:0 12px;display:inline-flex;align-items:center;gap:6px;cursor:pointer;
  background:var(--paper);border:1px solid var(--line);border-radius:7px;color:var(--ink-2);
  font:500 12.5px/1 "IBM Plex Sans",Arial,sans-serif}
.btn:hover{border-color:var(--accent);color:var(--accent-ink)}
.btn:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.btn.primary{background:var(--accent);border-color:var(--accent);color:var(--on-accent)}
.btn.primary:hover{opacity:.9;color:var(--on-accent)}
.spacer{flex:1}
.count{white-space:nowrap;color:var(--ink-3);font:500 12px/1 ui-monospace,"IBM Plex Mono",Consolas,monospace}

.table-wrap{background:var(--paper);border:1px solid var(--line);border-radius:10px;
  box-shadow:var(--shadow);overflow:auto}
table{border-collapse:separate;border-spacing:0;width:100%}
thead th{position:sticky;top:0;z-index:2;background:var(--paper);padding:11px 12px;text-align:left;
  color:var(--ink-3);white-space:nowrap;border-bottom:1px solid var(--line);
  box-shadow:inset 0 -1px 0 var(--line);
  font:600 10.5px/1.2 ui-monospace,"IBM Plex Mono",Consolas,monospace;letter-spacing:.08em;text-transform:uppercase}
thead th.cost{text-align:right}
tbody td{padding:9px 12px;border-bottom:1px solid var(--line-2);vertical-align:middle;color:var(--ink-2)}
tbody tr:last-child td{border-bottom:0}
tbody tr:hover td{background:var(--row-hover)}
tbody tr.assembly td{background:var(--accent-soft);color:var(--ink);font-weight:600}
tbody tr[hidden]{display:none}
td.cost{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
td.num{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap}
td.mono{white-space:nowrap;font:400 12px/1.4 ui-monospace,"IBM Plex Mono",Consolas,monospace}

.tree{display:flex;align-items:center;gap:2px;white-space:nowrap}
.rail{position:relative;flex:none;width:13px;align-self:stretch;min-height:20px}
.rail::before{content:"";position:absolute;left:6px;top:-9px;bottom:-9px;border-left:1px solid var(--line)}
.chev{flex:none;width:18px;height:18px;padding:0;border:0;background:none;cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;color:var(--accent);border-radius:4px;
  transition:transform .15s ease}
.chev:hover{background:var(--accent-soft)}
.chev:focus-visible{outline:2px solid var(--accent);outline-offset:1px}
.chev.open{transform:rotate(90deg)}
.chev.leaf{visibility:hidden}
.lvl{min-width:32px;text-align:right;color:var(--ink-3);font-variant-numeric:tabular-nums;
  font:500 11px/1 ui-monospace,"IBM Plex Mono",Consolas,monospace}
.glyph{flex:none;width:18px;text-align:center;font-size:12px}
.glyph.root{color:var(--accent)}
.glyph.asm{color:var(--accent-ink)}
.glyph.leafg{color:var(--ink-3)}

.key-link{color:var(--accent-ink);text-decoration:none;white-space:nowrap;
  border-bottom:1px solid transparent;
  font:600 12.5px/1 ui-monospace,"IBM Plex Mono",Consolas,monospace}
.key-link:hover{border-bottom-color:currentColor}

.pill{display:inline-flex;align-items:center;gap:5px;padding:2px 8px 2px 7px;border-radius:999px;
  white-space:nowrap;font:600 11px/1.6 "IBM Plex Sans",Arial,sans-serif}
.pill::before{content:"";width:5px;height:5px;border-radius:50%;background:currentColor}
.pill.ok{background:var(--ok-soft);color:var(--ok)}
.pill.warn{background:var(--warn-soft);color:var(--warn)}
.pill.hold{background:var(--hold-soft);color:var(--hold)}
.pill.plain{background:var(--line-2);color:var(--ink-2)}
.revchip{display:inline-block;padding:1px 7px;border-radius:5px;background:var(--line-2);color:var(--ink-2);
  font:600 11.5px/1.6 ui-monospace,"IBM Plex Mono",Consolas,monospace}

.files{display:flex;flex-direction:column;gap:6px}
.file{display:inline-flex;align-items:center;gap:8px;max-width:32ch;text-decoration:none;color:var(--ink)}
.file .ext{flex:none;width:26px;height:26px;border-radius:5px;display:grid;place-items:center;
  background:var(--accent-soft);color:var(--accent-ink);
  font:600 8.5px/1 ui-monospace,"IBM Plex Mono",Consolas,monospace}
.file .fname{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12.5px;
  border-bottom:1px solid transparent}
.file:hover .fname{border-bottom-color:var(--accent)}
.file .frev{display:block;color:var(--ink-3);
  font:400 11px/1.3 ui-monospace,"IBM Plex Mono",Consolas,monospace}
.empty{color:var(--ink-3)}

footer{display:flex;flex-wrap:wrap;gap:12px;justify-content:space-between;padding-top:13px;
  border-top:1px solid var(--line);color:var(--ink-3);font-size:11.5px}
footer code{font-family:ui-monospace,"IBM Plex Mono",Consolas,monospace}

@media (prefers-reduced-motion:reduce){*{transition:none!important}}
@media print{
  :root{--paper:#fff;--ground:#fff;--ink:#1c1930;--ink-2:#4b4661;--ink-3:#7d7794;
    --line:#e6e3ef;--line-2:#f1eff6;--accent:#5c35a8;--accent-soft:#f0ebfa;--accent-ink:#4a2a8a;
    --ok:#1f7a55;--ok-soft:#e4f3ec;--warn:#94620a;--warn-soft:#fbf1da;--hold:#8a4a6d;--hold-soft:#f8eaf1;
    --row-hover:#fff;--shadow:none}
  body{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .sheet{max-width:none;padding:0}
  .toolbar,.chev{display:none}
  .table-wrap,.facts{box-shadow:none;border-radius:0;overflow:visible}
  thead th{position:static}
  tbody tr{break-inside:avoid}
}`;

const EXPORT_FONTS =
  '<link rel="preconnect" href="https://fonts.googleapis.com">' +
  '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>' +
  '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600&display=swap">';

const SEARCH_ICON =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">' +
  '<circle cx="7" cy="7" r="4.5"></circle><path d="M10.5 10.5 14 14"></path></svg>';

// The tokens the server swaps for real filenames land in the page as text, so the
// extension tile — and the "this row has no file" fallback — can only be decided
// in the browser. Five lines, idempotent, shipped in every page including print.
const FILE_CHIP_SCRIPT = `<script>
(function(){
  var links=[].slice.call(document.querySelectorAll('a.file'));
  for(var i=0;i<links.length;i++){
    var a=links[i], n=a.querySelector('.fname'), t=(n&&n.textContent||'').trim();
    if(!t){ a.outerHTML='<span class="empty">&mdash;</span>'; continue; }
    var e=a.querySelector('.ext');
    if(e&&!e.textContent){ var m=t.match(/\\.([a-z0-9]{1,4})(?:\\s|$)/i); e.textContent=m?m[1].toUpperCase():'FILE'; }
  }
})();
<\/script>`;

function docShell({ title, eyebrow, stamp, facts, toolbar, table, foot, scripts }) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escHtml(title)}</title>
${EXPORT_FONTS}
<style>${EXPORT_CSS}</style>
</head>
<body>
<div class="sheet">
<header class="doc-head">
  <div>
    <p class="eyebrow">${escHtml(eyebrow)}</p>
    <h1>${escHtml(title)}</h1>
  </div>
  <div class="stamp">${stamp}</div>
</header>
${facts}
${toolbar}
<div class="table-wrap">
${table}
</div>
<footer>
  <span>${foot}</span>
  <span>Orcanos BOM Viewer &middot; a snapshot, not updated when Orcanos changes.</span>
</footer>
</div>
${scripts}
</body>
</html>`;
}

function factsHtml(items) {
  const cells = items
    .filter(Boolean)
    .map(
      (f) =>
        `<div class="fact${f.money ? ' money' : ''}"><dt>${escHtml(f.label)}</dt>` +
        `<dd>${escHtml(f.value)}${f.note ? ` <small>${escHtml(f.note)}</small>` : ''}</dd></div>`
    )
    .join('');
  return `<dl class="facts">${cells}</dl>`;
}

function toolbarHtml({ placeholder, tree }) {
  return `<div class="toolbar">
  <label class="search">${SEARCH_ICON}
    <input id="q" type="search" placeholder="${escHtml(placeholder)}" aria-label="Filter rows">
  </label>
  ${tree ? `<button class="btn" type="button" onclick="setAll(true)">Expand all</button>
  <button class="btn" type="button" onclick="setAll(false)">Collapse all</button>` : ''}
  <span class="spacer"></span>
  <span class="count" id="count"></span>
  <button class="btn primary" type="button" onclick="window.print()">Print / PDF</button>
</div>`;
}

// ─── Cell formatting ───────────────────────────────────────────────────────
// A column's TITLE is all the export knows about it, so every treatment below is
// offered on a title match and never assumed: an unrecognised column stays plain
// text, exactly as it rendered before.

const STATUS_TITLES = new Set([
  'status', 'state', 'routing state', 'change status', 'workflow status', 'item status',
]);
const REV_TITLES = new Set(['rev', 'revision', 'part revision', 'document revision']);
// A date is recognised by its VALUE, not its column title: "Updated Date" and
// "Updated By" both contain "updated", and only one of them is a date.
const DATE_VALUE = /^\d{1,4}[-/][A-Za-z0-9]{1,9}[-/]\d{1,4}(\s|T|$)/;

function isStatusCol(col) {
  return STATUS_TITLES.has(String(col.title || '').trim().toLowerCase());
}
function isRevCol(col) {
  return col.key === '__revision' || REV_TITLES.has(String(col.title || '').trim().toLowerCase());
}
function isDateValue(val) {
  return DATE_VALUE.test(String(val || '').trim());
}

// Semantic tone for a status value. Anything unrecognised is toned "plain" — a
// pill that shows the value without claiming to know what it means.
function pillTone(value) {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return 'plain';
  if (/^(approved|released|closed|completed|done|passed|effective|active)\b/.test(v)) return 'ok';
  if (/^(rejected|cancell?ed|void|obsolete|on hold|hold|failed|blocked)\b/.test(v)) return 'hold';
  if (/(draft|review|pending|progress|process|open|new|waiting|submitted)/.test(v)) return 'warn';
  return 'plain';
}

function renderCell(node, col, val) {
  if (COST_COLUMN_KEYS.has(col.key)) return `<td class="cost">${escHtml(val)}</td>`;
  if (isKeyCol(col)) {
    const url = buildItemUrl(node);
    const inner = url
      ? `<a class="key-link" href="${escHtml(url)}" target="_blank" rel="noopener">${escHtml(val)}</a>`
      : `<span class="key-link">${escHtml(val)}</span>`;
    return `<td>${inner}</td>`;
  }
  if (!val) return `<td><span class="empty">&mdash;</span></td>`;
  if (isStatusCol(col)) return `<td><span class="pill ${pillTone(val)}">${escHtml(val)}</span></td>`;
  if (isRevCol(col)) return `<td><span class="revchip">${escHtml(val)}</span></td>`;
  if (isDateValue(val)) return `<td class="mono">${escHtml(val)}</td>`;
  return `<td>${escHtml(val)}</td>`;
}

// In a zip the server swaps the tokens for files/<name> (or an Orcanos link when
// the file couldn't be read) and empties the ones it has no file for; anywhere
// else the link opens in Orcanos. A BOM row carries no `ecoFile` — the revision
// is the server's to choose there — so any DMS row gets a token.
function fileCellHtml(node, fileLinks) {
  const f = node.row.ecoFile;
  const tokenised = fileLinks && (f || node.row.type === 'DMS') && node.row.itemId;
  if (!tokenised && !f) return `<td><span class="empty">&mdash;</span></td>`;
  const rev = f ? `rev ${f.revision}${f.changeNo ? `.${f.changeNo}` : ''}` : '';
  const href = tokenised ? `__ECOFILE_HREF_${node.row.itemId}__` : f.url;
  const text = tokenised ? `__ECOFILE_NAME_${node.row.itemId}__` : f.name;
  // FILE_CHIP_SCRIPT fills the tile when the name is a token — only the browser
  // sees the filename the server substituted.
  const ext = tokenised ? '' : String(f.name || '').split('.').pop().slice(0, 4).toUpperCase();
  return (
    `<td><a class="file" href="${escHtml(href)}" target="_blank" rel="noopener">` +
    `<span class="ext">${escHtml(ext)}</span>` +
    `<span><span class="fname">${escHtml(text)}</span>` +
    (rev ? `<span class="frev">${escHtml(rev)}</span>` : '') +
    `</span></a></td>`
  );
}

// The server replaces the token with the row's attachment chips (or a dash when it found
// none); outside a zip there is nothing to show. A BOM zip asks for the non-DMS rows'
// attachments; an ECO zip (`dmsAttachments`) for the DMS rows' — a document's images and
// STEP models beside its file. A BOM zip (`'all'`) asks for both.
function attachCellHtml(node, fileLinks, dmsAttachments = false) {
  const r = node.row;
  if (!fileLinks || !r.itemId || r.type === 'CR'
      || (dmsAttachments !== 'all' && (r.type === 'DMS') !== dmsAttachments)) {
    return `<td><span class="empty">&mdash;</span></td>`;
  }
  return `<td>__ATTACHLIST_${String(r.itemId).replace(/\D/g, '')}__</td>`;
}

function exportedStamp(kind) {
  const now = new Date();
  const date = now.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  const time = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `<b>${escHtml(kind)}</b>Exported ${escHtml(date)} &middot; ${escHtml(time)}`;
}

// ─── Summary HTML generator ────────────────────────────────────────────────

function _generateSummaryHtml(entries, columns, { bomName, printMode }) {
  const nonQtyCols = summaryColumns(columns);
  const hasCost = entries.some((e) => e.unitCost > 0);
  const grandTotal = hasCost ? entries.reduce((s, e) => s + e.unitCost * e.totalQty, 0) : 0;
  const totalUnits = entries.reduce((s, e) => s + e.totalQty, 0);

  const thCells =
    nonQtyCols.map((c) => `<th>${escHtml(c.title)}</th>`).join('') +
    `<th class="cost">Total Qty</th><th class="cost">Occurrences</th>` +
    (hasCost ? `<th class="cost">Unit Cost</th><th class="cost">Total Cost</th>` : '');

  const bodyRows = entries
    .map((e, i) => {
      const cells = nonQtyCols
        .map((c) => renderCell(e.node, c, stripHtml(getFieldValue(e.node, c))))
        .join('');
      const costCells = hasCost
        ? `<td class="cost">$${escHtml(fmtCost(e.unitCost))}</td>` +
          `<td class="cost">$${escHtml(fmtCost(e.unitCost * e.totalQty))}</td>`
        : '';
      return (
        `<tr data-id="${i + 1}">${cells}` +
        `<td class="num">${escHtml(fmtQty(e.totalQty))}</td><td class="num">${e.count}</td>` +
        costCells +
        `</tr>`
      );
    })
    .join('\n');

  const facts = factsHtml([
    { label: 'Unique parts', value: String(entries.length) },
    { label: 'Total quantity', value: fmtQty(totalUnits), note: 'units' },
    hasCost && { label: 'Total cost', value: `$${fmtCost(grandTotal)}`, money: true },
  ]);

  const scripts = printMode
    ? `<script>window.addEventListener('load',function(){setTimeout(window.print,400);})<\/script>`
    : `<script>
var rows=[].slice.call(document.querySelectorAll('tbody tr[data-id]'));
var countEl=document.getElementById('count');
function apply(){
  var q=document.getElementById('q').value.trim().toLowerCase(),n=0;
  for(var i=0;i<rows.length;i++){
    var hit=!q||rows[i].textContent.toLowerCase().indexOf(q)!==-1;
    rows[i].hidden=!hit; if(hit)n++;
  }
  countEl.textContent=n+' of '+rows.length+' parts';
}
document.getElementById('q').addEventListener('input',apply);
apply();
<\/script>`;

  return docShell({
    title: bomName,
    eyebrow: 'Bill of Materials · Summary',
    stamp: exportedStamp('Summary'),
    facts,
    toolbar: printMode ? '' : toolbarHtml({ placeholder: 'Filter parts…', tree: false }),
    table: `<table>
<thead><tr>${thCells}</tr></thead>
<tbody>
${bodyRows}
</tbody>
</table>`,
    foot: `${entries.length} unique parts, rolled up across every occurrence.`,
    scripts,
  });
}

// ─── Hierarchy HTML generator ──────────────────────────────────────────────

function _generateHtml(nodes, columns, levelNumbers, { bomName, printMode, costs, fileLinks = false, dmsAttachments = false }) {
  const rootTotal = costs?.get(nodes[0].nodeKey)?.total || 0;

  // Sequential integers as stable HTML IDs.
  const htmlId = new Map();
  nodes.forEach((n, i) => htmlId.set(n.nodeKey, String(i + 1)));

  const hasKidsSet = new Set(nodes.filter((n) => n.parentKey).map((n) => n.parentKey));
  const docCount = nodes.filter((n) => n.row.type === 'DMS').length;

  const thCells =
    `<th>Tree</th>` +
    columns
      .map((c) => `<th${COST_COLUMN_KEYS.has(c.key) ? ' class="cost"' : ''}>${escHtml(c.title)}</th>`)
      .join('');

  const bodyRows = nodes
    .map((n) => {
      const id = htmlId.get(n.nodeKey);
      const pid = n.parentKey ? htmlId.get(n.parentKey) : '';
      const lNum = levelNum(n, levelNumbers);
      const hasKids = hasKidsSet.has(n.nodeKey);
      const hidden = !printMode && n.depth >= 2;

      // One rail per level above the row — the indent IS the hierarchy, so it is
      // drawn rather than implied by padding.
      const rails = new Array(Math.max(0, n.depth)).fill('<span class="rail"></span>').join('');

      const glyph = n.isRoot
        ? `<span class="glyph root" title="Root">&#9638;</span>`
        : hasKids
          ? `<span class="glyph asm" title="Assembly">&#9672;</span>`
          : `<span class="glyph leafg" title="Item">&#9671;</span>`;

      const chevron = printMode
        ? ''
        : `<button type="button" class="chev${hasKids ? (n.depth === 0 ? ' open' : '') : ' leaf'}"` +
          (hasKids
            ? ` onclick="toggle('${id}')" aria-label="Expand or collapse"`
            : ' tabindex="-1" aria-hidden="true"') +
          `><svg width="9" height="9" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">` +
          `<path d="M3 1l5 4-5 4z"></path></svg></button>`;

      const treeCell =
        `<td><div class="tree">${rails}${chevron}` +
        `<span class="lvl">${escHtml(lNum)}</span>${glyph}</div></td>`;

      const dataCells = columns
        .map((c) =>
          c.key === FILE_KEY
            ? fileCellHtml(n, fileLinks)
            : c.key === ATTACH_KEY
              ? attachCellHtml(n, fileLinks, dmsAttachments)
              : renderCell(n, c, stripHtml(getFieldValue(n, c, costs)))
        )
        .join('');

      return (
        `<tr data-id="${id}" data-parent="${pid}"${hasKids ? ' class="assembly"' : ''}` +
        `${hidden ? ' hidden' : ''}>${treeCell}${dataCells}</tr>`
      );
    })
    .join('\n');

  const facts = factsHtml([
    { label: 'Rows', value: String(nodes.length) },
    { label: 'Assemblies', value: String(hasKidsSet.size) },
    docCount > 0 && { label: 'Documents', value: String(docCount), note: 'DMS' },
    fileLinks && docCount > 0 && { label: 'Files', value: String(docCount), note: 'in package' },
    rootTotal > 0 && { label: 'Total cost', value: `$${fmtCost(rootTotal)}`, money: true },
  ]);

  const initExpanded = nodes
    .filter((n) => n.depth === 0 && hasKidsSet.has(n.nodeKey))
    .map((n) => `'${htmlId.get(n.nodeKey)}'`)
    .join(',');

  // Search reveals a match together with every ancestor that leads to it, so a
  // hit deep inside an assembly is never shown without the path that explains it.
  const interactive = `<script>
var rows=[].slice.call(document.querySelectorAll('tbody tr[data-id]'));
var expanded=new Set([${initExpanded}]);
var countEl=document.getElementById('count');
function toggle(id){
  expanded.has(id)?expanded.delete(id):expanded.add(id);
  var b=document.querySelector('tr[data-id="'+id+'"] .chev');
  if(b)b.classList.toggle('open',expanded.has(id));
  apply();
}
function setAll(open){
  for(var i=0;i<rows.length;i++){
    var pid=rows[i].getAttribute('data-parent');
    if(pid){ open?expanded.add(pid):expanded.delete(pid); }
  }
  var chevs=document.querySelectorAll('.chev');
  for(var j=0;j<chevs.length;j++){
    if(!chevs[j].classList.contains('leaf'))chevs[j].classList.toggle('open',open);
  }
  apply();
}
function apply(){
  var q=document.getElementById('q').value.trim().toLowerCase(),n=0,i;
  if(q){
    var keep=new Set();
    for(i=rows.length-1;i>=0;i--){
      var r=rows[i],rid=r.getAttribute('data-id');
      if(keep.has(rid)||r.textContent.toLowerCase().indexOf(q)!==-1){
        keep.add(rid);
        var p=r.getAttribute('data-parent'); if(p)keep.add(p);
      }
    }
    for(i=0;i<rows.length;i++){
      var on=keep.has(rows[i].getAttribute('data-id'));
      rows[i].hidden=!on; if(on)n++;
    }
  }else{
    var shown=new Set();
    for(i=0;i<rows.length;i++){
      var row=rows[i],pid=row.getAttribute('data-parent');
      var vis=!pid||(shown.has(pid)&&expanded.has(pid));
      row.hidden=!vis;
      if(vis){shown.add(row.getAttribute('data-id'));n++;}
    }
  }
  countEl.textContent=n+' of '+rows.length+' rows';
}
document.getElementById('q').addEventListener('input',apply);
apply();
<\/script>`;

  const scripts =
    (printMode
      ? `<script>window.addEventListener('load',function(){setTimeout(window.print,400);})<\/script>`
      : interactive) + FILE_CHIP_SCRIPT;

  return docShell({
    title: bomName,
    eyebrow: fileLinks ? 'Release package · Hierarchy and files' : 'Bill of Materials · Hierarchy',
    stamp: exportedStamp(fileLinks ? 'Package' : 'Hierarchy'),
    facts,
    toolbar: printMode
      ? ''
      : toolbarHtml({ placeholder: 'Filter by key, name or value…', tree: true }),
    table: `<table>
<thead><tr>${thCells}</tr></thead>
<tbody>
${bodyRows}
</tbody>
</table>`,
    foot: fileLinks
      ? 'Files in this package are the revisions that were effective at export; attachments are as they stood at export.'
      : `${nodes.length} rows across ${hasKidsSet.size} assemblies.`,
    scripts,
  });
}
